import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { NodeProps } from "reactflow";
import { Grid } from "semantic-ui-react";
import { useGetFacilityWholeList } from "../../../../../api/cultivations";
import { InputText } from "../../../../../components";
import TailwindSideModal from "../../../../../components/commonModal/TailwindSideModal";
import CommonSplitContainer from "../../../../../components/commonSplitContainer/CommonSplitContainer";
import { isNodeLabelExists } from "../../../../../components/drawing-tool/utils";
import DropDownWithButton from "../../../../../components/dropdownWithButton/DropDownWithButton";
import MainBottomMultipleButtonView from "../../../../../components/mainBottomButtonView/MainBottomMultipleButtonView";
import { NEW_FACILITY_KEY, NewFacilityOption } from "../../../../../config/constants";
import { FacilityType } from "../../../../../config/enums/FacilityType.enum";
import { NodeLabelExistError } from "../../../../../config/enums/NodeLabelError.enum";
import { errorView } from "../../../../../helpers/ErrorHandler";
import FacilityRegisterModal from "../../../../facility/facilityRegister/FacilityRegisterModal";
import ManageFacilityData from "../../../../facility/ManageFacilityData";

interface ProcessGateFormModalProps {
  setProcessGateNode: (node: NodeProps | null, isCreated: boolean) => void;
  newNode?: any;
  edit?: boolean;
  processGateName?: any;
  updateProcessGate: (processGateName: string, newNode: any) => void;
  createProcessGate: (processGateName: string, newNode: any) => void;
  chartNodes: any[];
}

const ProcessGateFormModalLegacy: React.FC<ProcessGateFormModalProps> = ({
  setProcessGateNode,
  newNode,
  edit,
  processGateName,
  updateProcessGate,
  createProcessGate,
  chartNodes
}) => {
  const [facilityId, setFacilityId] = useState("");
  const [visibleFacilityManager, setVisibleFacilityManager] = useState<boolean>(false);
  const [showFacilityRegister, setShowFacilityRegister] = useState<boolean>(false);

  const {
    data: facilityWholeList,
    isSuccess: isFacilityDataSuccess,
    refetch: refetchFacilityList
  } = useGetFacilityWholeList(FacilityType.FACTORY);

  const facilityListData = useMemo(() => {
    if (isFacilityDataSuccess) {
      if (facilityWholeList?.length > 0) {
        return [NewFacilityOption, ...facilityWholeList];
      }
      return [NewFacilityOption];
    }
    return [];
  }, [facilityWholeList, isFacilityDataSuccess]);

  const handleFacilityChange = (e: any, { value }: any) => {
    if (value === NEW_FACILITY_KEY) {
      setShowFacilityRegister(true);
      return;
    }
    setFacilityId(value);
    setValue("facilityName", value);
  };

  const defaultValues = useMemo(() => {
    return {
      processGateName: edit && processGateName ? processGateName : "",
      facilityName: edit && newNode?.data?.facilityId ? newNode.data.facilityId : ""
    };
  }, [edit, processGateName, newNode]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    clearErrors,
    setError,
    setFocus
  } = useForm({
    mode: "all",
    defaultValues
  });

  useEffect(() => {
    reset(defaultValues);
    if (edit && newNode?.data?.facilityId) {
      setFacilityId(newNode.data.facilityId);
    } else {
      setFacilityId("");
    }
  }, [edit, newNode, processGateName, reset, defaultValues]);

  const closeModal = () => {
    setProcessGateNode(null, false);
  };

  const onSubmit = (data: { processGateName: string; facilityName: string }) => {
    if (
      isNodeLabelExists(chartNodes as any, data?.processGateName) &&
      data?.processGateName !== processGateName
    ) {
      clearErrors("processGateName");
      setFocus("processGateName");
      setError("processGateName", { type: "manual", message: NodeLabelExistError.PROCESS_GATE });
      return;
    }

    if (!facilityId) {
      return errorView("Facility name is required");
    }

    const modifiedNode = {
      ...newNode,
      data: {
        ...newNode?.data,
        label: `Process Gate (${data.processGateName})`,
        facilityId: facilityId
      }
    };

    if (edit) {
      updateProcessGate(data.processGateName, modifiedNode);
    } else {
      createProcessGate(data.processGateName, modifiedNode);
    }
  };

  return (
    <>
      <TailwindSideModal
        open
        onOpenChange={open => !open && closeModal()}
        title={edit ? "Edit Process Gate" : "Add Process Gate"}
        contentClassName="sm:!tw-max-w-3xl"
      >
        <CommonSplitContainer
          topChild={
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid style={{ padding: "1rem" }}>
                <Grid.Column computer={8} tablet={8} mobile={8}>
                  <InputText
                    register={register}
                    labelName="Process Gate Name*"
                    name="processGateName"
                    errors={errors.processGateName}
                    required
                    errorMessage="Process Gate Name is required"
                  />
                </Grid.Column>
                <Grid.Column computer={8} tablet={8} mobile={16}>
                  <DropDownWithButton
                    labelName="Facility Name*"
                    placeholder="Select Facility Name"
                    currentData={facilityListData}
                    defaultValue={facilityId}
                    validate={true}
                    required={true}
                    errorMessage="Facility name is required"
                    customGridColumn="customGridColomnTyp"
                    handleChangeState={handleFacilityChange}
                    disabled={false}
                    buttonTitle="View"
                    buttonTheme="grey"
                    buttonOnClick={() => setVisibleFacilityManager(true)}
                    buttonDisabled={!facilityId}
                  />
                </Grid.Column>
                {showFacilityRegister && (
                  <FacilityRegisterModal
                    visibleModal={showFacilityRegister}
                    setVisibleModal={(open: boolean) => setShowFacilityRegister(open)}
                    refetchFacilityList={refetchFacilityList}
                  />
                )}
              </Grid>
            </form>
          }
          bottomChild={
            <div style={{ padding: "1rem" }}>
              <MainBottomMultipleButtonView
                leftButtons={[
                  {
                    title: "Cancel",
                    onClick: closeModal,
                    buttonOutLine: true
                  }
                ]}
                rightButtons={[
                  {
                    contentView: 16,
                    title: edit ? "Update" : "Create",
                    onClick: handleSubmit(onSubmit),
                    buttonView: true
                  }
                ]}
              />
            </div>
          }
        />
      </TailwindSideModal>
      {visibleFacilityManager && facilityId && (
        <ManageFacilityData
          facilityId={facilityId}
          closeManager={() => setVisibleFacilityManager(false)}
        />
      )}
    </>
  );
};

export default ProcessGateFormModalLegacy;
